import React, { useState } from "react";
import { Alert, Card, Empty, Space, Tabs, Typography } from "antd";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import type { RootState } from "../../app/store";
import SelectOrganizationsLight from "../components/select/SelectOrganizationsLight";
import NotFoundPage from "../unauthorized/NotFoundPage";
import { isMasterAdmin } from "../../utils/isMasterAdmin";
import { isSimulationEnvironment } from "../../utils/simulationEnvironment";
import SimulationFilesTab from "./SimulationFilesTab";
import SimulationScenariosTab from "./SimulationScenariosTab";
import SimulationRunsTab from "./SimulationRunsTab";

/**
 * The development-only Simulation console.
 *
 * A field engineer picks an organization, keeps its library of Wireshark
 * captures and KONE OPC UA domain files, builds scenarios that replay them
 * against several banks at once, and runs them through the organization's
 * web sandbox: simulator → tunnel → VTS → edge → this cloud. The page exists
 * only where `isSimulationEnvironment()` allows it, and only for master
 * administrators; anywhere else the route renders the not-found page, so a
 * production build never shows that it exists.
 */
function SimulationPage() {
  const [t] = useTranslation("global");
  const { userLogged, theme } = useSelector((state: RootState) => state.home);
  const [orgId, setOrgId] = useState("");

  if (!isSimulationEnvironment() || !isMasterAdmin(userLogged)) {
    return <NotFoundPage />;
  }

  const needsOrganization = (
    <Empty
      description={t("simulation.selectOrganizationFirst")}
      image={Empty.PRESENTED_IMAGE_SIMPLE}
    />
  );

  return (
    <div className={`layout__contentPage__${theme}`}>
      <Space direction="vertical" className="generalStyles__width100">
        <Alert type="info" showIcon message={t("simulation.developmentOnly")} />
        <Card size="small">
          <Space direction="vertical" className="generalStyles__width100">
            <Typography.Text strong>{t("simulation.organization")}</Typography.Text>
            <SelectOrganizationsLight
              value={orgId}
              onChangeIdOrganization={(id: string) => setOrgId(id)}
              disabledInput={false}
            />
          </Space>
        </Card>
        <Tabs
          items={[
            {
              key: "scenarios",
              label: t("simulation.scenarios"),
              children: orgId ? <SimulationScenariosTab orgId={orgId} /> : needsOrganization,
            },
            {
              key: "runs",
              label: t("simulation.runs"),
              children: orgId ? <SimulationRunsTab orgId={orgId} /> : needsOrganization,
            },
            {
              key: "files",
              label: t("simulation.files"),
              children: orgId ? <SimulationFilesTab orgId={orgId} /> : needsOrganization,
            },
          ]}
        />
      </Space>
    </div>
  );
}

export default SimulationPage;
